import "./index.less";
import React, { FC, HTMLAttributes, PropsWithChildren, ReactNode } from "react";
import cs from "classnames";

export type BadgeUIStatus =
  | "default"
  | "success"
  | "processing"
  | "warning"
  | "error";

export interface BadgeUIProps
  extends PropsWithChildren<HTMLAttributes<HTMLSpanElement>> {
  count?: ReactNode;
  dot?: boolean;
  status?: BadgeUIStatus;
  text?: ReactNode;
}

const isVisibleCount = (count: ReactNode) => {
  return count !== undefined && count !== null && count !== false && count !== 0;
};

const getLimitedCount = (count: ReactNode) => {
  if (typeof count === "number") {
    return count > 9 ? 9 : count;
  }

  if (typeof count === "string" && /^\d+$/.test(count)) {
    const numberCount = Number(count);
    return numberCount > 9 ? 9 : count;
  }

  return count;
};

const isOverLimitCount = (count: ReactNode) => {
  if (typeof count === "number") {
    return count > 9;
  }

  if (typeof count === "string" && /^\d+$/.test(count)) {
    return Number(count) > 9;
  }

  return false;
};

const BadgeUI: FC<BadgeUIProps> = ({
  children,
  className,
  count,
  dot,
  status,
  text,
  ...restProps
}) => {
  const hasChildren = children !== undefined && children !== null;
  const showBadge = dot
    ? count === undefined || isVisibleCount(count)
    : isVisibleCount(count);
  const overLimitCount = isOverLimitCount(count);
  const displayCount = getLimitedCount(count);
  const rootClassName = cs(
    "badge-ui",
    {
      "badge-ui-status": !hasChildren,
    },
    className
  );

  if (!hasChildren) {
    return (
      <span {...restProps} className={rootClassName}>
        {status ? (
          <span
            className={cs(
              "badge-ui-status-dot",
              `badge-ui-status-${status}`
            )}
          />
        ) : null}
        {text !== undefined ? (
          <span className="badge-ui-status-text">
            {text}
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <span {...restProps} className={rootClassName}>
      {children}
      {showBadge ? (
        <span
          className={cs("badge-ui-dot", {
            "badge-ui-number": !dot,
            "badge-ui-number-limit": !dot && overLimitCount,
          })}
        >
          {dot ? null : (
            <>
              <span className="badge-ui-number-value">{displayCount}</span>
              {overLimitCount ? (
                <span className="badge-ui-number-plus" aria-hidden="true">
                  +
                </span>
              ) : null}
            </>
          )}
        </span>
      ) : null}
    </span>
  );
};

export default BadgeUI;
